import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';
import EditMobileNumberModal from './EditMobileNumberModal';
import { notifyProfilePhotoUpdated } from '../../utils/profileEvents';

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp';

const getFileExtension = (fileName = '') => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
};

const isAllowedPhotoFile = (file) => {
  if (!file) return false;
  if (ALLOWED_PHOTO_TYPES.includes(file.type)) return true;
  return ALLOWED_PHOTO_EXTENSIONS.includes(getFileExtension(file.name));
};

const validatePhotoFile = (file) => {
  if (!file) {
    return 'Please select a photo to upload.';
  }
  if (file.size === 0) {
    return 'The selected file is empty. Please choose another image.';
  }
  if (!isAllowedPhotoFile(file)) {
    return 'Please upload a JPG, PNG, GIF, or WebP image.';
  }
  if (file.size > MAX_PHOTO_SIZE_BYTES) {
    return 'Photo must be 5MB or smaller.';
  }
  return null;
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  const detail = data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => item?.msg || item?.message).filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  if (data?.message) return data.message;
  return null;
};

const getPhotoUrlFromResponse = (response, fallback = null) => {
  return (
    response?.profile?.photo ||
    response?.data?.profile?.photo ||
    response?.photo ||
    response?.data?.photo ||
    fallback
  );
};

const normalizeProfile = (profile = {}) => ({
  firstName: profile.first_name || '',
  lastName: profile.last_name || '',
  email: profile.email || '',
  mobileNumber: profile.mobile_number || '',
  jobTitle: profile.job_title || '',
  company: profile.company || '',
  bio: profile.bio || '',
  photo: profile.photo || null,
});

const ProfileSettings = () => {
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    jobTitle: '',
    company: '',
    bio: '',
    photo: null
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [initialProfileData, setInitialProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    jobTitle: '',
    company: '',
    bio: '',
    photo: null
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(false);
  const photoInputRef = useRef(null);
  const photoPreviewUrlRef = useRef(null);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [phoneModalStep, setPhoneModalStep] = useState('request');
  const [newMobileNumber, setNewMobileNumber] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneModalError, setPhoneModalError] = useState('');
  const [isRequestingPhoneOtp, setIsRequestingPhoneOtp] = useState(false);
  const [isVerifyingPhoneOtp, setIsVerifyingPhoneOtp] = useState(false);
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSaving || isRemovingPhoto) return;
    setIsDragging(true);
  }, [isRemovingPhoto, isSaving]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const revokePhotoPreview = useCallback(() => {
    if (photoPreviewUrlRef.current) {
      URL.revokeObjectURL(photoPreviewUrlRef.current);
      photoPreviewUrlRef.current = null;
    }
  }, []);

  const resetPhotoInput = useCallback(() => {
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  }, []);

  const applyProfile = useCallback((profile) => {
    const normalizedProfileData = normalizeProfile(profile);
    setProfileData(normalizedProfileData);
    setInitialProfileData(normalizedProfileData);
    setPhotoLoadError(false);
    return normalizedProfileData;
  }, []);

  const processSelectedPhoto = useCallback((file) => {
    const validationError = validatePhotoFile(file);
    if (validationError) {
      toast.error(validationError);
      setErrors((prev) => ({ ...prev, avatar: validationError }));
      resetPhotoInput();
      return;
    }

    revokePhotoPreview();
    const previewUrl = URL.createObjectURL(file);
    photoPreviewUrlRef.current = previewUrl;
    setPhotoFile(file);
    setPhotoLoadError(false);
    setProfileData((prev) => ({
      ...prev,
      photo: previewUrl,
    }));
    setErrors((prev) => ({ ...prev, avatar: null }));
    resetPhotoInput();
  }, [resetPhotoInput, revokePhotoPreview]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isSaving || isRemovingPhoto) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) {
      toast.error('No file was dropped. Please try again.');
      return;
    }

    processSelectedPhoto(files[0]);
  }, [isRemovingPhoto, isSaving, processSelectedPhoto]);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await axiosInstance.get('/user_profile/profile_detail');
        applyProfile(response?.profile);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Error fetching profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [applyProfile]);

  useEffect(() => () => revokePhotoPreview(), [revokePhotoPreview]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processSelectedPhoto(file);
  };

  const handleRemovePhoto = async () => {
    if (isSaving || isRemovingPhoto) return;

    // Unsaved local selection: discard preview and restore last saved photo.
    if (photoFile) {
      revokePhotoPreview();
      setPhotoFile(null);
      setPhotoLoadError(false);
      setProfileData((prev) => ({
        ...prev,
        photo: initialProfileData.photo,
      }));
      setErrors((prev) => ({ ...prev, avatar: null }));
      resetPhotoInput();
      return;
    }

    if (!initialProfileData.photo) {
      setProfileData((prev) => ({ ...prev, photo: null }));
      return;
    }

    setIsRemovingPhoto(true);
    try {
      const response = await axiosInstance.delete('/user_profile/remove-photo');

      if (response.status === true || response.status === 200 || response.status_code === 200) {
        revokePhotoPreview();
        setProfileData((prev) => ({
          ...prev,
          photo: null,
        }));
        setInitialProfileData((prev) => ({
          ...prev,
          photo: null,
        }));
        setPhotoFile(null);
        setPhotoLoadError(false);
        resetPhotoInput();
        notifyProfilePhotoUpdated(null);
        toast.success(response.message || 'Photo removed successfully!');
      } else {
        toast.error(response.message || 'Failed to remove photo');
      }
    } catch (error) {
      console.error('Error removing photo:', error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail?.message ||
        'Error removing photo'
      );
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  const resetPhoneModalState = useCallback(() => {
    setPhoneModalStep('request');
    setNewMobileNumber(profileData.mobileNumber || '');
    setPhoneOtp('');
    setPhoneModalError('');
    setIsRequestingPhoneOtp(false);
    setIsVerifyingPhoneOtp(false);
  }, [profileData.mobileNumber]);

  const openPhoneModal = () => {
    resetPhoneModalState();
    setIsPhoneModalOpen(true);
  };

  const closePhoneModal = () => {
    if (isRequestingPhoneOtp || isVerifyingPhoneOtp) return;
    setIsPhoneModalOpen(false);
    resetPhoneModalState();
  };

  const validatePhoneNumber = (value) => /^\d{10}$/.test(value);

  const hasEditableProfileChanges = useMemo(() => {
    return (
      profileData.firstName !== initialProfileData.firstName ||
      profileData.lastName !== initialProfileData.lastName ||
      profileData.jobTitle !== initialProfileData.jobTitle ||
      profileData.company !== initialProfileData.company ||
      profileData.bio !== initialProfileData.bio ||
      profileData.photo !== initialProfileData.photo ||
      photoFile !== null
    );
  }, [initialProfileData, photoFile, profileData]);

  const handleRequestPhoneOtp = async () => {
    if (!validatePhoneNumber(newMobileNumber)) {
      setPhoneModalError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setPhoneModalError('');
    setIsRequestingPhoneOtp(true);

    try {
      const response = await axiosInstance.post('/users/request_phone_otp', {
        mobile_number: newMobileNumber,
      });

      if (response?.status === 200 || response?.status === true || response?.status_code === 200) {
        setPhoneModalStep('verify');
        toast.success(response?.message || 'OTP sent successfully.');
      } else {
        setPhoneModalError(
          response?.detail?.message || response?.message || 'Failed to send OTP. Please try again.'
        );
      }
    } catch (error) {
      setPhoneModalError(
        error?.response?.data?.detail?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to send OTP. Please try again.'
      );
    } finally {
      setIsRequestingPhoneOtp(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (phoneOtp.length !== 6) {
      setPhoneModalError('Please enter the 6-digit OTP.');
      return;
    }

    setPhoneModalError('');
    setIsVerifyingPhoneOtp(true);

    try {
      const response = await axiosInstance.post('/users/verify_phone_otp', {
        mobile_number: newMobileNumber,
        otp: phoneOtp,
      });

      if (response?.status === 200 || response?.status === true || response?.status_code === 200) {
        setProfileData((prev) => ({
          ...prev,
          mobileNumber: newMobileNumber,
        }));
        setInitialProfileData((prev) => ({
          ...prev,
          mobileNumber: newMobileNumber,
        }));
        setIsPhoneModalOpen(false);
        resetPhoneModalState();
        toast.success(response?.message || 'Mobile number updated successfully.');
      } else {
        setPhoneModalError(
          response?.detail?.message || response?.message || 'OTP verification failed. Please try again.'
        );
      }
    } catch (error) {
      setPhoneModalError(
        error?.response?.data?.detail?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'OTP verification failed. Please try again.'
      );
    } finally {
      setIsVerifyingPhoneOtp(false);
    }
  };


  const validateForm = () => {
    const newErrors = {};

    if (photoFile) {
      const photoError = validatePhotoFile(photoFile);
      if (photoError) {
        newErrors.avatar = photoError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    revokePhotoPreview();
    setPhotoFile(null);
    setPhotoLoadError(false);
    setErrors({});
    setProfileData(initialProfileData);
    resetPhotoInput();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (photoFile) {
      const photoError = validatePhotoFile(photoFile);
      if (photoError) {
        toast.error(photoError);
        return;
      }
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append('first_name', profileData.firstName || '');
      formData.append('last_name', profileData.lastName || '');
      if (profileData.mobileNumber) {
        formData.append('mobile_number', profileData.mobileNumber);
      }
      formData.append('job_title', profileData.jobTitle || '');
      formData.append('company', profileData.company || '');
      formData.append('bio', profileData.bio || '');

      // Only send the file when adding or replacing a photo. Omitting it
      // keeps the existing photo; an empty photo field can wipe it.
      if (photoFile) {
        formData.append('photo', photoFile, photoFile.name);
      }

      const response = await axiosInstance.put(
        '/user_profile/user/profile',
        formData,
        {
          skipGlobalToast: true,
        }
      );

      const isSuccess =
        Boolean(response) &&
        response.status !== false &&
        (typeof response.status_code !== 'number' || response.status_code < 400);

      if (!isSuccess) {
        toast.error(response?.message || response?.detail?.message || 'Failed to update profile');
        return;
      }

      revokePhotoPreview();
      setPhotoFile(null);
      resetPhotoInput();

      let savedProfile = null;
      try {
        const refreshed = await axiosInstance.get('/user_profile/profile_detail');
        savedProfile = applyProfile(refreshed?.profile);
      } catch (refreshError) {
        const fallbackPhoto = photoFile
          ? getPhotoUrlFromResponse(response, initialProfileData.photo)
          : profileData.photo;
        savedProfile = {
          ...profileData,
          photo: fallbackPhoto,
        };
        setProfileData(savedProfile);
        setInitialProfileData((prev) => ({
          ...prev,
          firstName: savedProfile.firstName,
          lastName: savedProfile.lastName,
          jobTitle: savedProfile.jobTitle,
          company: savedProfile.company,
          bio: savedProfile.bio,
          photo: savedProfile.photo,
        }));
        setPhotoLoadError(false);
      }

      notifyProfilePhotoUpdated(savedProfile?.photo || null);
      toast.success(response?.message || 'Profile updated successfully!');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Error updating profile');
    } finally {
      setIsSaving(false);
    }
  };

  const showPhotoPreview = Boolean(profileData.photo) && !photoLoadError;
  const isPhotoBusy = isSaving || isRemovingPhoto;

  // if (isLoading) {
  //   return <div className='card-stat' style={{ display: 'flex', justifyContent: 'center' }}>
  //     <i className='fas fa-spinner fa-spin'></i>
  //   </div>;
  // }

  return (
    <div className="settings-section">
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Profile</h2>
        <p className="settings-subtitle">Update your personal information and avatar.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div
          className="profile-avatar-section"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className={`profile-avatar ${isDragging ? 'dragging' : ''}`}>
            {showPhotoPreview ? (
              <img
                src={profileData.photo}
                alt="User avatar"
                onError={() => setPhotoLoadError(true)}
              />
            ) : (
              <div className="avatar-placeholder">
                <svg
                  className="avatar-placeholder-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 21a8 8 0 0 0-16 0"></path>
                  <circle cx="12" cy="8" r="4"></circle>
                </svg>
              </div>
            )}
            {isDragging && !isPhotoBusy && (
              <div className="drag-overlay">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <p>Drop your image here</p>
              </div>
            )}
          </div>
          <div className="profile-avatar-actions">
            <label className={`avatar-upload-btn ${isPhotoBusy ? 'disabled' : ''}`}>
              <input
                ref={photoInputRef}
                type="file"
                accept={PHOTO_ACCEPT}
                style={{ display: 'none' }}
                onChange={handlePhotoUpload}
                disabled={isPhotoBusy}
              />
              {initialProfileData.photo || photoFile ? 'Change Photo' : 'Upload Photo'}
            </label>
            {(profileData.photo || photoFile) && (
              <button
                type="button"
                className="avatar-remove-btn"
                onClick={handleRemovePhoto}
                disabled={isPhotoBusy}
              >
                {isRemovingPhoto ? 'Removing...' : photoFile ? 'Discard Photo' : 'Remove Photo'}
              </button>
            )}
            <p className="avatar-upload-hint">JPG, PNG, GIF, or WebP. Max 5MB.</p>
          </div>
          {errors.avatar && <div className="form-error">{errors.avatar}</div>}
        </div>

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={profileData.firstName}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={profileData.lastName}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                readOnly
                disabled
                // onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="mobileNumber">Mobile Number</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={profileData.mobileNumber}
                  readOnly
                  disabled
                  placeholder="No mobile number added"
                  className="pr-28"
                />
                <button
                  type="button"
                  className="absolute right-3 z-10 inline-flex h-8 items-center gap-1.5 rounded-full bg-blue-50 px-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  onClick={openPhoneModal}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 20h9"></path>
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                  </svg>
                  Edit
                </button>
              </div>
              {errors.mobileNumber && (
                <div className="form-error">{errors.mobileNumber}</div>
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="jobTitle">Job Title</label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={profileData.jobTitle}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label htmlFor="company">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                value={profileData.company}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="bio">Bio</label>
          <textarea
            id="bio"
            name="bio"
            rows="4"
            value={profileData.bio}
            onChange={handleInputChange}
          ></textarea>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleCancel}
            disabled={isSaving || !hasEditableProfileChanges}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSaving || !hasEditableProfileChanges}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <EditMobileNumberModal
        isOpen={isPhoneModalOpen}
        onClose={closePhoneModal}
        currentNumber={profileData.mobileNumber}
        newMobileNumber={newMobileNumber}
        onMobileNumberChange={setNewMobileNumber}
        otp={phoneOtp}
        onOtpChange={setPhoneOtp}
        step={phoneModalStep}
        isRequestingOtp={isRequestingPhoneOtp}
        isVerifyingOtp={isVerifyingPhoneOtp}
        onRequestOtp={handleRequestPhoneOtp}
        onVerifyOtp={handleVerifyPhoneOtp}
        onResendOtp={handleRequestPhoneOtp}
        error={phoneModalError}
      />
    </div>
  );
};

export default ProfileSettings;